import type { UseStepperReturn } from "@vueuse/core"
import type {
  CategorySummary,
  CellCoord,
  CellErrorMsg,
  EditItems,
  ExternalNames,
  IgnoredNames,
  ImportData,
  ItemInfo,
  MatchSummaryStatus,
  MatchTab,
  NewNames,
  Step,
  UploadFile,
} from "@/types"
import type { FileChannelString } from "@/utils/fileImport"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { MultiTypeParseRequest } from "@kaleidoscope-solutions/engine-wasm"
import { DialogSize } from "@kaleidoscope-solutions/ks-design-system"
import { useFileDialog, useStepper } from "@vueuse/core"
import { DateTime } from "luxon"
import { defineStore } from "pinia"

import { markRaw } from "vue"

import { openDialog } from "vue3-promise-dialog"

import MissingInfoImage from "@/assets/pngs/Confused.png"
import PictogramDialog from "@/components/Dialog/PictogramDialog.vue"
import Locate from "@/components/FileImport/Locate.vue"
import Match from "@/components/FileImport/Match.vue"
import Preview from "@/components/FileImport/Preview.vue"
import {
  Decorator,
  ImportTargetType,
  ItemMatchStatus,
  MatchType,
  NOT_IN_FILE,
  PhaseSteps,
} from "@/types"
import {
  getDecorator,
  objectToCsv,
  stringArrayToLower,
  stringKeyedMapToLower,
} from "@/utils"
import { ALPHA_ATTR_LOCATION, ALPHA_COMPONENT, ALPHA_VARIANT_ID } from "@/utils/alphaModel"
import useAnalytics from "@/utils/analytics"
import { multiple_file_warning } from "@/utils/errors"

import { LocateChannel } from "@/utils/fileImport"

import {
  autoLocateColumnHeaders,
  cellCoord,
} from "@/utils/fileImport/autolocate"
import {
  DirtyState,
  InteractionInfo,
  InteractionMap,
} from "@/utils/fileImport/interactions"
import { readCSV, readXlsxFile } from "@/utils/fileImport/upload"
import { captureError } from "@/utils/useEngineModel/ModelFacade"

const categoriesToAutoMatch = [
  ALPHA_VARIANT_ID,
  ALPHA_ATTR_LOCATION,
  ALPHA_COMPONENT
]

const { trackEvent } = useAnalytics()
interface State {
  loading: boolean
  blockLoading: boolean
  model?: ModelFacade
  dialog: boolean
  file?: UploadFile
  fileChannel?: FileChannelString
  importTargets: { datasets: string[]; categories: string[] }
  targets?: string[]
  targetType?: ImportTargetType
  targetKeyTypes?: string[] // used to order categories on Locate & Match steps
  valueColumns: number[]
  weightingColumn?: number
  locateInfo: LocateFileTarget
  itemMatchInfo: ItemMatchInfo
  locateTooltips: Map<CategoryName, string>
  notInFileMap: Map<CategoryName, string>
  currentMatchTab: number
  dateFormatMap: Map<CategoryName, string>
  stepInteractions: InteractionMap<PhaseSteps>
  locateColumnInteractions: InteractionMap<CategoryName>
  locateValueInteraction: InteractionInfo
  matchTabInteractions: InteractionMap<CategoryName>
  valueColumnInvalidValues: Set<string>
  validTargetsInvalidValues: Set<string>[]
  invalidValueCount: number
  dateColumnsWithMixedContent: boolean[]
  editBanner: boolean
  editItems: undefined | EditItems
  errorSheets: boolean
  durationAnalytics: Map<string, number>
  timeCategory: string | undefined
  openFileFullDialog: boolean
}

export type CategoryName = string
type CategoryItemName = string
type LocateFileTarget = Map<CategoryName, number>
type ItemMatchInfo = Map<CategoryName, ItemInfo[]>

const locateChannel = new LocateChannel()

const fileImportDialog = useFileDialog({ accept: ".tsv, .csv, .txt, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })

export const useFileImportStore = defineStore("fileImport", {
  state: (): State => ({
    loading: false,
    blockLoading: false,
    model: undefined,
    dialog: false,
    file: undefined,
    fileChannel: undefined,
    importTargets: { datasets: [], categories: [] },
    targets: undefined,
    targetType: undefined,
    targetKeyTypes: undefined,
    valueColumns: [],
    locateInfo: new Map<string, number>(),
    notInFileMap: new Map<string, string>(),
    itemMatchInfo: new Map<string, ItemInfo[]>(),
    locateTooltips: new Map<string, string>(),
    currentMatchTab: 0,
    dateFormatMap: new Map<string, string>(),
    stepInteractions: new InteractionMap(),
    locateColumnInteractions: new InteractionMap(),
    locateValueInteraction: new InteractionInfo(DirtyState.Clean, false),
    matchTabInteractions: new InteractionMap(),
    valueColumnInvalidValues: new Set(),
    validTargetsInvalidValues: [],
    invalidValueCount: 0,
    dateColumnsWithMixedContent: [],
    editBanner: false,
    editItems: undefined,
    errorSheets: false,
    durationAnalytics: new Map<string, number>(),
    timeCategory: undefined,
    openFileFullDialog: false,
  }),
  actions: {
    initialise(model: ModelFacade) {
      this.model = model

      fileImportDialog.onChange(async (files) => {
        if (files && files.length > 0) {
          this.loading = true
          this.dialog = true
          try {
            await this.handleFileUpload(Array.from(files), false)
          } catch (error) {
            console.error(error)
          }

          this.loading = false
        }
      })
    },
    async retrieveTimeCategory() {
      try {
        const timeCategory = await this.modelApi.timeCategory()
        this.timeCategory = timeCategory.name
      } catch {
        this.timeCategory = undefined
      }
    },
    fileSelect() {
      fileImportDialog.open()
    },
    reset(extraPersistKeys: Array<keyof State> = []) {
      // Temporary store some states.
      const persistKeys: Array<keyof State> = [
        "model",
        "timeCategory",
        ...extraPersistKeys
      ]

      const persistedValues = Object.fromEntries(persistKeys.map((key) => [key, this[key]]))

      // Reset the store to the default state.
      this.$reset()

      // Restore persisted values.
      this.$patch(persistedValues)

      // Reset the stepper to go back to the first step.
      this.stepper.goTo(this.steps[0])
    },
    resetLocateInteractions() {
      this.locateColumnInteractions = new InteractionMap()
      this.locateValueInteraction = new InteractionInfo(DirtyState.Clean, false)
    },
    resetMatchInteractions() {
      this.matchTabInteractions = new InteractionMap()
    },
    goToNext() {
      this.stepper.goToNext()
    },

    goToStep(phaseStep: PhaseSteps) {
      const foundStep = this.stepper.steps.value.find((step) => step.title === phaseStep)
      if (!foundStep) {
        console.warn("Step not found", phaseStep)
        return
      }

      const previousStep = this.stepper.current.value.title
      this.markStepTouched(previousStep)

      if (previousStep === PhaseSteps.PREVIEW) {
        this.durationAnalytics.set("previewCumulativeDuration", this.getPreviewDuration)
      }

      const steps = Object.values(PhaseSteps)
      const currentStepIndex = steps.indexOf(phaseStep) - 1

      if (currentStepIndex > -1) {
        for (let i = currentStepIndex - 1; i >= 0; i--) {
          const step = steps[i]

          if (step === PhaseSteps.PREVIEW) {
            this.durationAnalytics.set("previewStartTime", new Date().valueOf())
          }
          this.markStepTouched(step)
        }
      }
      this.stepper.goTo(foundStep)
    },
    markStepTouched(phaseStep: PhaseSteps) {
      this.stepInteractions.markTouched(phaseStep)

      switch (phaseStep) {
        case PhaseSteps.SELECT:
          if (this.targetKeyTypes !== undefined) {
            this.locateColumnInteractions.markAllTouched(this.targetKeyTypes)
          }
          this.locateValueInteraction.markTouched()
          break
        case PhaseSteps.MATCH:
          if (this.targetKeyTypes !== undefined) {
            this.matchTabInteractions.markAllTouched(this.targetKeyTypes)
          }
          break
      }
    },
    setLocateValueColumn(
      valueColumns: number[],
      dirty: DirtyState = DirtyState.Dirty
    ) {
      this.valueColumns = valueColumns

      this.stepInteractions.markDirty(PhaseSteps.SELECT)
      this.locateValueInteraction.markDirty(dirty)
    },
    setLocateValueColumnByIndex(
      targetIndex: number,
      columnIndex: number,
      dirty: DirtyState = DirtyState.Dirty
    ) {
      this.valueColumns[targetIndex] = columnIndex

      this.stepInteractions.markDirty(PhaseSteps.SELECT)
      this.locateValueInteraction.markDirty(dirty)
    },
    setWeightingColumn(weightingColumn: number) {
      this.weightingColumn = weightingColumn
    },
    setLocateColumnForCategory(
      categoryName: CategoryName,
      column: number,
      dirty: DirtyState = DirtyState.Dirty
    ) {
      this.locateInfo.set(categoryName, column)
      // Remove match info when item is set to repopulate matches.
      this.itemMatchInfo.delete(categoryName)

      // Remove any populated date format maps when locate changes.
      if (categoryName === this.timeCategory) {
        this.dateFormatMap.set(categoryName, "")
      }

      this.notInFileMap.delete(categoryName)
      this.stepInteractions.markDirty(PhaseSteps.SELECT)
      this.locateColumnInteractions.markDirty(categoryName, dirty)
      // Update match information if locate information changes.
      this.populateMatchDataFromLocate()
    },
    setLocateNotInFileForCategory(
      categoryName: CategoryName,
      categoryItemName: CategoryItemName
    ) {
      this.notInFileMap.set(categoryName, categoryItemName)
      this.itemMatchInfo.delete(categoryName)
      this.stepInteractions.markDirty(PhaseSteps.SELECT)
      this.locateColumnInteractions.markDirty(categoryName)

      // Update match information if locate information changes.
      // TODO: This is not awaited, does it matter?
      this.populateMatchDataFromLocate()
    },
    async setTargetByType(targets: string[], type: ImportTargetType) {
      if (type === ImportTargetType.Dataset) {
        await this.setTargetDataset(targets)
        return
      }

      await this.setTargetCategory(targets)
    },
    resetTarget() {
      this.targets = undefined
      this.targetKeyTypes = undefined
      this.targetType = undefined
    },
    async getImportTargets() {
      if (this.importTargets.datasets.length > 0 || this.importTargets.categories.length > 0) {
        return
      }

      this.importTargets = await this.model!.getImportTargets()
    },
    async setTargetDataset(datasetNames: string[]) {
      this.blockLoading = true
      if (datasetNames.length === 0) {
        this.resetTarget()
        this.blockLoading = false
        return
      }

      if (this.targets && this.targets[0] !== datasetNames[0]) {
        this.removeTarget()
      }

      this.targets = datasetNames
      this.targetType = ImportTargetType.Dataset

      this.targetKeyTypes = (await this.modelApi.dataset(datasetNames[0])).keyTypes
      this.valueColumns = Array.from({ length: datasetNames.length })

      this.stepInteractions.markDirty(PhaseSteps.UPLOAD)

      await this.setValidDatasetTargets()
      // TODO: this is not awaited, Does it matter?
      await this.autoLocate()
      this.loading = false
      this.blockLoading = false
    },
    setTargetCategory(categoryNames: string[]) {
      this.removeTarget()

      this.targets = categoryNames
      this.targetType = ImportTargetType.Category
      this.targetKeyTypes = [categoryNames[0]]

      this.stepInteractions.markDirty(PhaseSteps.UPLOAD)
      // TODO: this is not awaited, Does it matter?
      this.autoLocate()
    },
    async setValidDatasetTargets() {
      if (!this.targets || !this.file?.scraped || !this.isDatasetTarget) {
        return
      }

      this.validTargetsInvalidValues = []
      const columnLength = this.file.scraped?.headers.length ?? 0

      const request = new MultiTypeParseRequest()

      // Only need to get the first target, all targets should have the same valueTypes.
      const datasetName = this.targets[0]
      const datasetValueType = (await this.modelApi.dataset(datasetName)).valueTypes[0]

      // Add all columns and their values to the MultiTypeParseRequest
      for (let col = 0; col < columnLength; col++) {
        // Get column values and filter out empty strings
        const columnValues = getValuesInColumn(col, this.file).filter((v) => v !== "")

        // Add column values to check against the dataset type of the target
        request.add(datasetValueType, columnValues)
      }

      // Send the request to the engine for validation
      const columnTypeValidationRes = await this.modelApi.canParseMultipleToTypes(request)

      // Process the response to determine invalid values for each column
      for (let col = 0; col < columnLength; col++) {
        // Get column values and filter out empty strings
        const columnValues = getValuesInColumn(col, this.file).filter((v) => v !== "")
        const invalidValues = new Set<string>()

        columnValues.forEach((value) => {
          const isColumnValueValid = columnTypeValidationRes.get(col, value)
          if (!isColumnValueValid) {
            invalidValues.add(value)
          }
        })

        this.validTargetsInvalidValues[col] = invalidValues
      }
    },
    async handleFileUpload(files: File[], reset: boolean = true) {
      if (reset) {
        this.reset()
      }

      try {
        if (files.length > 1) {
          const ok = await multiple_file_warning()
          if (!ok) {
            return
          }
        }

        await this.defaultImportTarget()
        await this.loadFiles(files)
        this.dialog = true
        this.loading = true

        // Go to the first invalid step. If all steps are valid go to the Preview.
        if (!this.valueAndAllCategoriesLocated) {
          // Go to the Select step without marking it as touched.
          this.stepper.goTo(PhaseSteps.SELECT)
          this.loading = false
          return
        }

        if (!(this.allInFileCategoriesLocated && this.allItemsMatched)) {
          this.goToStep(PhaseSteps.MATCH)
          this.loading = false
          return
        }

        this.goToStep(PhaseSteps.PREVIEW)
      } catch (error) {
        this.dialog = false
        throw error
      } finally {
        this.loading = false
      }
    },
    // The store currently only support one file, so the first file uploaded will be used.
    async loadFiles(files: File[]) {
      this.errorSheets = false

      try {
        const firstFile = files[0]

        let file: UploadFile

        if (
          firstFile.type === "text/csv" ||
          firstFile.type === "text/tab-separated-values" ||
          // Firefox uses the registered application to determine the MIME type.
          firstFile.name.endsWith(".csv") ||
          firstFile.name.endsWith(".tsv")
        ) {
          file = await readCSV(firstFile)
        } else {
          file = await readXlsxFile(firstFile)
        }

        await this.addFile(file)
      } catch (error) {
        this.errorSheets = true
        throw error
      }
    },

    async addFile(file: UploadFile) {
      // Store a local copy as we might need to manipulate the file later.
      const localFile = file
      // Reset auto located file channel.
      this.fileChannel = undefined

      if (localFile?.scraped) {
        const channel = locateChannel.identifyChannel(
          localFile?.scraped.headers
        )

        if (channel) {
          this.fileChannel = channel

          // Each channel can have a formatter to change the file columns on upload.
          // The main use case is we need to combine two columns together for
          // files from an Etsy export.

          const channelConfig = locateChannel.getChannelConfig(channel)
          if (typeof channelConfig.formatter === "function") {
            localFile.scraped = channelConfig.formatter(localFile.scraped)
          }

          trackEvent(
            "GDI_file_match_channel",
            { Channel: channel }
          )
        } else {
          // Track when we don't have a channel config.
          trackEvent(
            "GDI_file_match_channel",
            { Channel: "unrecognised" }
          )
        }
      }

      this.file = localFile

      this.stepInteractions.markDirty(PhaseSteps.UPLOAD)
      await this.setValidDatasetTargets()

      await this.autoLocate()
    },
    removeTarget() {
      this.reset([
        "loading",
        "blockLoading",
        "dialog",
        "file",
        "fileChannel",
      ])
    },
    removeFile() {
      const targets = this.targets
      const targetType = this.targetType
      this.reset(["dialog", "loading", "blockLoading"])
      if (targets && targetType !== undefined) {
        // TODO: This is not awaited, does it need to be?
        this.setTargetByType(targets, targetType)
      }

      this.dialog = true
    },
    async autoLocate() {
      if (!this.targets || !this.file?.scraped || !this.targetKeyTypes) {
        return
      }

      // Auto locate target column if there is only one option, regardless of name matching
      if (this.file.scraped?.headers.length === 1 && this.isCategoryTarget) {
        this.setLocateValueColumn(Array.from({ length: this.targets.length }), DirtyState.Guess)
        this.setLocateColumnForCategory(this.targets[0], 0, DirtyState.Guess)
        return
      }

      if (this.getValidTargetsCount === 1) {
        const colIdx = this.validTargetsInvalidValues.findIndex(
          (invalidValues) => invalidValues !== undefined
        )
        this.setLocateValueColumn([colIdx], DirtyState.Guess)
      }

      await this.populateFromChannelConfig()

      await this.autoLocateColumnForTimeCategory()

      for (const categoryName of categoriesToAutoMatch) {
        this.autoMatch(categoryName)
      }

      autoLocateColumnHeaders(
        this.modelApi,
        this.file,
        this.targets,
        this.targetKeyTypes
      ).forEach((columnIndex, targetName) => {
        if (this.isCategoryTarget) {
          if (isColumnEmpty(columnIndex, this.file!)) {
            return
          }

          this.setLocateValueColumn([columnIndex], DirtyState.Guess)
          this.setLocateColumnForCategory(
            targetName,
            columnIndex,
            DirtyState.Guess
          )
          return
        }

        const targetIndex = (this.targets ?? []).indexOf(targetName)

        if (targetIndex !== -1) {
          // TODO(Yulin): now we could have multiple targets, does this needs to be a InteractionMap?
          // see issue https://github.com/kaleidoscope-solutions/ks-ui-web/issues/1540
          // if (this.locateValueInteraction.isDirty()) {
          //   return
          // }

          if (this.validTargetsInvalidValues[columnIndex] === undefined) {
            return
          }

          this.setLocateValueColumnByIndex(targetIndex, columnIndex, DirtyState.Guess)
          return
        }

        if (!this.locateColumnInteractions.isDirty(targetName)) {
          if (isColumnEmpty(columnIndex, this.file!)) {
            return
          }

          this.locateColumnInteractions.markTouched(targetName)
          this.setLocateColumnForCategory(
            targetName,
            columnIndex,
            DirtyState.Guess
          )
        }
      })

      // starts the stopwatch
      this.durationAnalytics.set("importStartTime", new Date().valueOf())
    },
    // TODO #489 - temp fix to remove
    // https://github.com/kaleidoscope-solutions/ks-ui-web/issues/489
    // TEMP FIX hard code to set variant ID or Location (both import by external names) to created and matched
    autoMatch(elementName: string) {
      const elements = this.itemMatchInfo.get(elementName)

      if (!elements) {
        return
      }

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i]
        if (element.status !== ItemMatchStatus.PENDING) {
          return
        }

        this.setMatchStatus(
          elementName,
          i,
          element.externalName,
          ItemMatchStatus.CREATE
        )
      }
    },
    async populateFromChannelConfig() {
      if (!this.file?.scraped?.headers || !this.targets || !this.fileChannel) {
        return false
      }

      try {
        for (let i = 0; i < this.targets.length; i++) {
          const target = this.targets[i]

          const channel = locateChannel.getChannelBlockConfig(
            this.fileChannel,
            this.file?.scraped?.headers,
            target
          )

          this.setLocateValueColumnByIndex(i, Number(channel.target), DirtyState.Guess)

          if (channel.weighting) {
            this.setWeightingColumn(Number(channel.weighting))
          }

          // Handle Column mappings
          if (channel.columns) {
            const columnMappings = Object.keys(channel.columns)
            if (columnMappings.length > 0) {
              for (const key of columnMappings) {
                const column = channel.columns[key]
                this.setLocateColumnForCategory(key, Number(column), DirtyState.Guess)
              }
            }
          }

          // Handle NotInFile mappings
          if (channel.notInFile) {
            const notInFileMappings = Object.keys(channel.notInFile)
            if (notInFileMappings.length > 0) {
              for (const key of notInFileMappings) {
                const value = channel.notInFile[key]
                this.setLocateColumnForCategory(key, NOT_IN_FILE, DirtyState.Guess)
                this.notInFileMap.set(key, `${value}`)
              }
            }
          }

          // Set date format after columns are located.
          const dateFormats = Object.keys(channel.dateFormats)
          if (dateFormats.length > 0) {
            for (const category of dateFormats) {
              const dateFormat = channel.dateFormats[category]

              const parsed = await this.parsedDates(category, dateFormat)

              const categoryItems = this.itemMatchInfo.get(category) ?? []
              const externalNames = categoryItems.map((item) => item.externalName)

              // Check if all the external names are matched when dates are parsed
              // in the engine.
              if (parsed.size === externalNames.length) {
                this.dateFormatMap.set(category, dateFormat)
                this.setDateMatches(category, parsed, true)
                // set initial touch state
                this.matchTabInteractions.markDirty(category, DirtyState.Guess)

                trackEvent(
                  "GDI_date_format",
                  { "date format": dateFormat }
                )
              } else {
                // If all the dates don't match we try to guess and only set if
                // unambiguous
                const guessedParsed = await this.guessDateFormat(category)

                if (guessedParsed !== undefined) {
                  this.dateFormatMap.set(category, guessedParsed.format)
                  this.setDateMatches(category, guessedParsed.parsed, true)
                  // set initial touch state
                  this.matchTabInteractions.markDirty(category, DirtyState.Guess)

                  trackEvent(
                    "GDI_guessed_date_format",
                    { date_format_expected: dateFormat,
                      date_format_guessed: guessedParsed.format
                    }
                  )
                }
              }
            }
            this.stepInteractions.markDirty(PhaseSteps.MATCH)

            this.markStepTouched(PhaseSteps.MATCH)
          }
        }
        return true
      } catch (error) {
        captureError(error)
        return false
      }
    },
    goToMatchTab(categoryName: string) {
      const tabs = this.matchTabs

      this.currentMatchTab = tabs.findIndex((tab) => tab.title === categoryName)
    },
    markCurrentMatchTabTouched() {
      const tabs = this.matchTabs

      if (this.currentMatchTab > tabs.length - 1) {
        return
      }
      this.matchTabInteractions.markTouched(tabs[this.currentMatchTab].title)
    },
    async populateMatchDataFromLocate() {
      if (
        this.itemMatchInfo.size > 0 &&
        // Regenerate match info if an item has been changed into the locate screen
        // keep this gate to stop regenerating every time we switch steps
        this.locateInfo.size === this.itemMatchInfo.size
      ) {
        // eslint-disable-next-line no-console
        console.debug("item match info already exists, skipping populate")
        return
      }
      if (!this.file?.scraped) {
        console.warn("file missing, no matches possible")
        return
      }
      if (this.locateInfo.size === 0) {
        console.warn("locate mappings missing, no matches possible")
        return
      }
      const data = this.file?.scraped.rows
      const locations = this.locateInfoInFile

      const categoriesWithoutMatchData = Array.from(locations.keys()).filter(
        (categoryName, _) => !this.itemMatchInfo.has(categoryName)
      )

      // Assume from sample files in https://miro.com/app/board/uXjVMcmytoo=/?moveToWidget=3458764549466035793&cot=14

      for (const categoryName of categoriesWithoutMatchData) {
        const colIndex = locations.get(categoryName)!
        const uniqueItems = this.extractUniqueItems(colIndex, data)
        if (categoryName === "Channel") {
          await this.handleChannelCategoryTempFix(categoryName, uniqueItems)
        } else {
          this.itemMatchInfo.set(
            categoryName,
            uniqueItems.map((name) => ({ externalName: name, status: ItemMatchStatus.PENDING }))
          )
          await this.populateMatchStatus(categoryName)
        }

        this.matchTabInteractions.markTouched(categoryName)

        if (categoriesToAutoMatch.includes(categoryName)) {
          this.autoMatch(categoryName)
        }
      }

      this.stepInteractions.markDirty(PhaseSteps.MATCH)
    },

    // TEMP FIX: Channel category handling (to be removed in future)
    async handleChannelCategoryTempFix(this: any, categoryName: string, uniqueItems: string[]) {
      const externalNamesMap = await this.modelApi.externalNameMap("Channel")
      this.itemMatchInfo.set(
        categoryName,
        uniqueItems.map((name) => {
          let internalName: string = name
          let status: ItemMatchStatus

          if (externalNamesMap.has(name)) {
            internalName = externalNamesMap.get(name)!
            status = ItemMatchStatus.EXISTING_MATCH
          } else {
            status = ItemMatchStatus.CREATE
          }

          return { externalName: name, internalName, status }
        })
      )
    },

    extractUniqueItems(colIndex: number, rows: string[][]) {
      const uniqueItems: Set<string> = new Set()
      rows.forEach((row) => uniqueItems.add(row[colIndex].trim()))

      uniqueItems.delete("") // remove any blank

      return Array.from(uniqueItems)
    },
    async populateMatchStatus(categoryName: string) {
      // If Channel, treat as always matched (auto-created)
      if (categoryName === "Channel") {
        return
      }
      const [itemNames, externalNames, ignoredNames, importByExternalName]: [
        itemNames: string[],
        externalNames: Map<string, string>,
        ignoredNames: string[],
        importByExternalName: boolean,
      ] = await Promise.all([
        this.modelApi.itemNames(categoryName),
        this.modelApi.externalNameMap(categoryName),
        this.modelApi.ignoredNames(categoryName),
        this.modelApi.getImportByExternalNames(categoryName),
      ])

      const itemMatchInfoForCategory = this.setItemMatchStatus(
        this.itemMatchInfo.get(categoryName) ?? [],
        itemNames,
        externalNames,
        ignoredNames,
        importByExternalName
      )

      this.itemMatchInfo.set(categoryName, itemMatchInfoForCategory)

      this.groupAndSortMatches(categoryName)
    },
    setItemMatchStatus(
      itemMatchInfo: ItemInfo[],
      itemNames: string[],
      externalNames: Map<string, string>,
      ignoredNames: string[],
      importByExternalName: boolean
    ) {
      // TODO(Matt): This only works with specific locales which don't require case folding / normalisation.
      // investigate localeCompare https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
      const lowerItemNames = stringArrayToLower(itemNames)
      const lowerExternalNames = stringKeyedMapToLower(externalNames)
      const lowerIgnoredNames = stringArrayToLower(ignoredNames)

      return itemMatchInfo.map((itemInfo) => {
        const externalName = itemInfo.externalName.toLowerCase()

        if (lowerExternalNames?.has(externalName)) {
          // Exact match between name in the file and external name. User has
          // matched Ecuador Bananas to Bananas in a previous import.
          // Status is: Existing Match

          itemInfo.internalName = lowerExternalNames.get(externalName) as
          | string |
          undefined
          itemInfo.status = ItemMatchStatus.EXISTING_MATCH
        } else if (lowerIgnoredNames.includes(externalName)) {
          // Set status of previously ignored items
          itemInfo.status = ItemMatchStatus.ALREADY_IGNORED
        } else if (lowerItemNames.includes(externalName) && !importByExternalName) {
          // Exact match between name in the file and standard name. There is no
          // matching external name. The User has not matched Banana to Banana in
          // previous import, but the name in the file is an exact match for the
          // standard name in the system. Status is: Auto-match
          // We don't consider cases where import_by_external_names is set because
          // want want to create the name even if it already exists as an internal
          // name.
          itemInfo.internalName =
            itemNames[lowerItemNames?.indexOf(externalName)]
          itemInfo.status = ItemMatchStatus.AUTO_MATCH
        } else {
          itemInfo.status = ItemMatchStatus.PENDING
        }
        return itemInfo
      })
    },

    groupAndSortMatches(categoryName: string) {
      const items = this.itemMatchInfo.get(categoryName)
      if (!items) {
        return
      }

      // This only contains the statuses which are automatically populated,
      // as no user actions have occurred before the table is displayed
      const groupOrder = [
        ItemMatchStatus.PENDING,
        ItemMatchStatus.AUTO_MATCH,
        ItemMatchStatus.EXISTING_MATCH,
        ItemMatchStatus.ALREADY_IGNORED,
      ]

      const groupedItems = groupOrder.map((status) =>
        items.filter((item) => item.status === status)
      )
      const sortedItems = groupedItems.map(this.sortMatchItemsGroup)

      this.itemMatchInfo.set(categoryName, sortedItems.flat())
    },
    sortMatchItemsGroup(items: ItemInfo[] | undefined) {
      if (!items) {
        return []
      }

      return items.sort((a, b) => {
        if (a.externalName < b.externalName) {
          return -1
        }
        if (a.externalName > b.externalName) {
          return 1
        }
        return 0
      })
    },

    setMatchToIgnore(categoryName: string, rowIndex: number) {
      this.setMatchStatus(categoryName, rowIndex, "", ItemMatchStatus.TO_IGNORE)
    },
    async figureOutUpdatedStatus(
      categoryName: string,
      item: ItemInfo,
      itemName: string
    ) {
      const externalName = await this.modelApi.externalNameMap(categoryName)
      const previousMatchName = externalName.get(item.externalName) ?? undefined

      let status = ItemMatchStatus.MANUAL_MATCH

      if (itemName === "") {
        // If the map to system name is blank
        status = ItemMatchStatus.PENDING
      } else if (this.getCreatedItems(categoryName).includes(itemName)) {
        // If the map is in the new created list
        status = ItemMatchStatus.CREATE
      } else if (previousMatchName === itemName) {
        // If the map matches the previous import external name
        status = ItemMatchStatus.EXISTING_MATCH
      } else if (
        previousMatchName !== undefined &&
        previousMatchName !== itemName
      ) {
        // If the map matches the previous import external name
        status = ItemMatchStatus.EDIT_MATCH
      }

      return status
    },
    setMatchStatus(
      categoryName: string,
      rowIndex: number,
      itemName: string,
      status: ItemMatchStatus
    ) {
      const categoryItems = this.itemMatchInfo.get(categoryName)

      if (categoryItems === undefined) {
        return
      }

      categoryItems[rowIndex].internalName = itemName
      categoryItems[rowIndex].status = status
      this.itemMatchInfo.set(categoryName, categoryItems)
      this.stepInteractions.markDirty(PhaseSteps.MATCH)
    },
    async getCategoryItems(categoryName: string): Promise<string[]> {
      return await this.modelApi.itemNames(categoryName)
    },
    getCreatedItems(categoryName: string): string[] {
      const list = (this.itemMatchInfo.get(categoryName) ?? [])
        .filter(
          (item) =>
            item.status === ItemMatchStatus.CREATE &&
            item.internalName !== undefined
        )
        .map((item) => item.internalName as string)

      return [...new Set(list)]
    },
    renameNewMatches(
      categoryName: string,
      rowIndex: number,
      newItemName: string
    ) {
      const categoryItems = this.itemMatchInfo.get(categoryName)

      if (categoryItems === undefined) {
        return
      }

      const oldName = categoryItems[rowIndex].internalName

      const updatedCategoryItems = categoryItems.map((item) => {
        if (item.internalName === oldName) {
          item.internalName = newItemName
        }
        return item
      })

      this.itemMatchInfo.set(categoryName, updatedCategoryItems)
    },
    async autoLocateColumnForTimeCategory() {
      // Auto locate Time Category if there is only one valid column
      const timeLiterals = await this.modelApi.timeLiterals()
      const targetTimeKey = this.targetKeyTypes?.filter((key) =>
        timeLiterals.includes(key)
      )

      if (targetTimeKey?.length !== 1) {
        return
      }

      const validTimeColumns = this.getValidTimeColumns

      if (validTimeColumns.length !== 1) {
        return
      }

      this.setLocateColumnForCategory(
        targetTimeKey[0],
        validTimeColumns[0],
        DirtyState.Guess
      )
    },
    importDataIntoModel() {
      const jsonData = JSON.stringify(this.importData)
      return this.modelApi.import(jsonData)
    },
    resetMatches(categoryName: string) {
      const categoryItems = this.itemMatchInfo.get(categoryName) ?? []

      const items = categoryItems.map((item: ItemInfo) => {
        item.internalName = ""
        item.status = ItemMatchStatus.PENDING
        return item
      })

      this.itemMatchInfo.set(categoryName, items)
    },
    async guessDateFormat(categoryName: string) {
      const categoryItems = this.itemMatchInfo.get(categoryName) ?? []
      const externalNames = categoryItems.map((item) => item.externalName)

      // Get all a list of date formats from the engine.
      const dateFormats = await this.modelApi.dateFormats()

      // We need to keep track to check if more than one format matches.
      const formatMatches = []
      let parsedDates = new Map<string, string>()

      for (let i = 0; i < dateFormats.length; i++) {
        const dateFormat = dateFormats[i]

        const parsed = await this.parsedDates(categoryName, dateFormat)

        if (parsed && parsed.size === externalNames.length) {
          formatMatches.push(dateFormat)
          parsedDates = parsed
        }

        if (formatMatches.length > 1) {
          // return early is more than one valid match is found.
          return
        }
      }

      // Set matches only if parsed is set.
      if (parsedDates.size === externalNames.length) {
        return {
          format: formatMatches[0],
          parsed: parsedDates,
        }
      }
    },

    setDateMatches(
      categoryName: string,
      parsedDates: Map<string, string>,
      guessed?: boolean
    ) {
      const categoryItems = this.itemMatchInfo.get(categoryName) ?? []

      const matchedCategoryItems = categoryItems.map((item) => {
        const internalName = parsedDates.get(item.externalName)
        item.internalName = internalName

        item.status = internalName
          ? ItemMatchStatus.MANUAL_MATCH
          : ItemMatchStatus.TO_IGNORE

        // Apply auto state
        if (guessed) {
          item.status = ItemMatchStatus.AUTO_MATCH
        }

        return item
      })

      this.itemMatchInfo.set(categoryName, matchedCategoryItems)
    },
    async parsedDates(categoryName: string, dateFormat: string) {
      const categoryItems = this.itemMatchInfo.get(categoryName) ?? []

      const externalNames = categoryItems.map((item) => item.externalName)

      const parsed = await this.modelApi.parseDates(dateFormat, externalNames)

      return parsed
    },
    async dateFormats(): Promise<any[]> {
      return await this.modelApi.dateFormats()
    },
    async defaultImportTarget() {
      const importTargets = await this.model!.getImportTargets()

      const importTargetTypeLookup: Map<string, ImportTargetType> = new Map(
        importTargets.datasets.map((target) => [
          target,
          ImportTargetType.Dataset,
        ])
      )

      // Auto select a target when the target isn't set and only one target is available.
      if (
        // Only auto set the target when no target is set.
        this.targets === undefined &&
        // Auto set target when only one is available.
        importTargets.datasets.length === 1
      ) {
        const target = importTargets.datasets[0]
        const targetType = importTargetTypeLookup.get(target)
        if (targetType) {
          await this.setTargetByType([target], targetType)
        }
      }
    },
    async openExitConfirmationDialog() {
      const ok = await openDialog<any>(PictogramDialog, {
        open: true,
        size: DialogSize.medium,
        confirmDialog: true,
        okButton: "Yes, close and lose progress",
        imagePath: MissingInfoImage,
        title: "Warning. All progress will be lost",
        body: [
          " If you close this import, <strong>all changes you have made will be lost.</strong>. ",
          " Are you sure you want to close the import process?",
        ],
      })

      if (ok) {
        this.closeDialog()
      }
    },
    closeDialog() {
      this.dialog = false
      // Reset import when dialog closes.
      this.reset()
    },
    isCategoryMatched(categoryName: string) {
      return isCategoryMatched(categoryName, this.itemMatchInfo)
    },
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    isDatasetTarget(): boolean {
      return this.targetType === ImportTargetType.Dataset
    },
    isCategoryTarget(): boolean {
      return this.targetType === ImportTargetType.Category
    },
    steps(): Step<PhaseSteps>[] {
      return [
        {
          title: PhaseSteps.SELECT,
          component: markRaw(Locate),
          showNext: () => true,
          decorator: () =>
            getDecorator(
              this.stepInteractions.get(PhaseSteps.SELECT),
              this.valueAndAllCategoriesLocated
            ),
        },
        {
          title: PhaseSteps.MATCH,
          component: markRaw(Match),
          showNext: () => true,
          decorator: () =>
            getDecorator(
              this.stepInteractions.get(PhaseSteps.MATCH),
              // located has to be valid for match to work
              this.allInFileCategoriesLocated && this.allItemsMatched
            ),
        },
        {
          title: PhaseSteps.PREVIEW,
          component: markRaw(Preview),
          showNext: () => true,
          decorator: () => Decorator.NOT_APPLICABLE,
        },
      ]
    },
    stepper(): UseStepperReturn<any, Step<PhaseSteps>[], Step<PhaseSteps>> {
      return useStepper<any>(this.steps)
    },
    matchTabs(state): MatchTab[] {
      const timeCategory = state.timeCategory
      // guarantee order is the same as Locate dropdowns

      // TEMP fix remove VARIANT ID
      // TODO #489 - temp fix to remove
      // https://github.com/kaleidoscope-solutions/ks-ui-web/issues/489
      // remove variant IDS from tabs list
      const reduceList = this.targetKeyTypes?.filter((category) =>
        this.locateInfoInFile.has(category)
      ).filter((category) =>
        // Remove locate step from auto-matched categories
        !categoriesToAutoMatch.map((c) => c.toLowerCase()).includes(category.toLowerCase())
      )

      return (
        reduceList?.map((categoryName, index): MatchTab => {
          const type =
            categoryName === timeCategory ? MatchType.Time : MatchType.Text

          const decorator = () =>
            getDecorator(
              this.matchTabInteractions.get(categoryName),
              isCategoryMatched(categoryName, this.itemMatchInfo)
            )
          return { title: categoryName, id: index, type, decorator }
        }) || []
      )
    },
    hasTarget(): boolean {
      return this.targets !== undefined && this.targets.length > 0
    },
    isFileUploaded(): boolean {
      return this.file !== undefined
    },
    isTargetAndUploadValid(): boolean {
      return this.hasTarget && this.isFileUploaded
    },
    invalidValuesInTargetColumn(): Map<CellCoord, CellErrorMsg> {
      if (
        !this.valueColumns ||
        !this.file ||
        !this.isDatasetTarget
      ) {
        return new Map<CellCoord, CellErrorMsg>()
      }

      const invalidCellMap = new Map<CellCoord, CellErrorMsg>()

      this.valueColumns.forEach((valueColumn, targetIndex) => {
        if (valueColumn === undefined || this.validTargetsInvalidValues[targetIndex] === undefined) {
          return
        }

        const invalidValues = this.validTargetsInvalidValues[targetIndex] as Set<string>
        if (!this.file) {
          return
        }
        const valueColumnCells = getValuesInColumn(valueColumn, this.file)

        // Collect any cells with invalid values in the value column.
        valueColumnCells.forEach((cell, rowIndex) => {
          const coord = cellCoord(rowIndex, valueColumn)
          if (invalidValues.has(cell)) {
            invalidCellMap.set(coord, "invalid type")
          }
        })
      })

      this.invalidValueCount = invalidCellMap.size

      return invalidCellMap
    },
    anyValueColumnValuesIsValid(): boolean {
      if (this.valueColumns === undefined || !this.file) {
        return false
      }

      return !getValuesInColumn(this.valueColumns[0], this.file).every((value) =>
        this.valueColumnInvalidValues.has(value)
      )
    },
    locateValueColumnDecorator(): Decorator {
      return getLocateDecorator(
        this.stepInteractions.get(PhaseSteps.SELECT),
        this.locateValueInteraction,
        this.isValueColumnMapped
      )
    },
    isValueColumnMapped(): boolean {
      const mapped = this.valueColumns !== undefined
      if (!mapped) {
        return false
      }

      return this.anyValueColumnValuesIsValid
    },
    allCategoriesLocated(): boolean {
      if (!this.targetKeyTypes) {
        return false
      }

      return this.targetKeyTypes.every((categoryName) =>
        isCategoryLocated(categoryName, this.locateInfo, this.notInFileMap)
      )
    },
    /**
     * Check if all keytypes are located but ignores not in file.
     * @returns boolean
     */
    allInFileCategoriesLocated(): boolean {
      if (!this.targetKeyTypes) {
        return false
      }
      return this.targetKeyTypes.every((categoryName) => {
        const target = this.locateInfo.get(categoryName)

        return target !== undefined
      })
    },
    valueAndAllCategoriesLocated(): boolean {
      return this.allCategoriesLocated && this.isValueColumnMapped
    },
    allItemsMatched(): boolean {
      // Check Match preconditions -- we shouldn't decorate as valid
      // until all these have had a chance to be initialised
      const haveLocateData =
        this.itemMatchInfo.size + this.notInFileMap.size !== 0

      if (!this.isTargetAndUploadValid || !haveLocateData) {
        return false
      }

      // Match is valid if all categories are 'Not in file'
      if (this.targetKeyTypes?.length === this.notInFileMap.size) {
        return true
      }

      for (const itemMatchInfo of this.itemMatchInfo) {
        if (!isCategoryMatched(itemMatchInfo[0], this.itemMatchInfo)) {
          return false
        }
      }

      return true
    },
    locateInfoInFile(): LocateFileTarget {
      return removeNotInFileFromLocateInfo(this.locateInfo)
    },
    importData() {
      const data: ImportData = {
        targets: this.targets ?? [],
        columnMap: Object.fromEntries(this.locateInfoInFile),
        valueColumns: this.valueColumns ?? [],
        externalNames: externalNamesFromMatchInfo(this.itemMatchInfo),
        ignoredNames: ignoredNamesFromMatchInfo(this.itemMatchInfo),
        notInFileMap: Object.fromEntries(this.notInFileMap),
        newNames: {
          ...newNamesFromNotInFile(
            this.model as ModelFacade,
            this.notInFileMap
          ),
          ...newNamesFromMatchInfo(this.itemMatchInfo),
        },
        dataFormat: this.fileChannel,
        data: this.file?.scraped ? objectToCsv(this.file?.scraped) : "",
      }

      if (this.weightingColumn) {
        data.weightingColumn = this.weightingColumn
      }

      return data
    },
    locateDecorator(): (categoryName: CategoryName) => Decorator {
      return (categoryName: CategoryName) =>
        getLocateDecorator(
          this.stepInteractions.get(PhaseSteps.SELECT),
          this.locateColumnInteractions.get(categoryName),
          isCategoryLocated(categoryName, this.locateInfo, this.notInFileMap)
        )
    },
    matchDecorator(): (categoryName: CategoryName) => Decorator {
      return (categoryName: CategoryName) =>
        getDecorator(
          this.matchTabInteractions.get(categoryName),
          isCategoryMatched(categoryName, this.itemMatchInfo)
        )
    },
    locateSummary(): {
      category: string[]
      decorator: Decorator
      ignoredCount?: number
    }[] {
      const locateTarget = {
        category: this.targets ?? [],
        decorator: this.locateValueColumnDecorator,
        ignoredCount: this.invalidValueCount,
      }

      if (this.isCategoryTarget) {
        return [locateTarget]
      }

      const locateKeyTypes: { category: string[]; decorator: Decorator }[] = (
        this.targetKeyTypes ?? []
      ).map((category: string) => {
        const decorator = this.locateDecorator(category)
        return {
          category: [category],
          decorator,
        }
      })

      return [locateTarget, ...locateKeyTypes]
    },
    matchSummary() {
      const summary: CategorySummary[] = []

      for (const [categoryName, itemInfos] of this.itemMatchInfo) {
        if (this.notInFileMap.get(categoryName)) {
          continue
        }

        // Get remove duplicate items with the status Create
        const uniqueCreatedItemsCount = new Set(
          itemInfos
            .filter((item) => item.status === ItemMatchStatus.CREATE)
            .map((item) => item.internalName)
        ).size

        const statusCount: MatchSummaryStatus = itemInfos.reduce(
          (result, item) => {
            const currentCount = result.get(item.status) ?? 0
            result.set(item.status, currentCount + 1)
            return result
          },
          new Map() as MatchSummaryStatus
        )

        const totalCreated = statusCount.get(ItemMatchStatus.CREATE) ?? 0
        const totalMatched = statusCount.get(ItemMatchStatus.MANUAL_MATCH) ?? 0

        const totalCreateMatches = totalCreated + totalMatched

        let createdMatchedLabel = ""

        if (totalCreateMatches !== 0) {
          createdMatchedLabel = `${totalCreateMatches} ${getItemMatchStatusLabel(
            ItemMatchStatus.MANUAL_MATCH
          )}`
        }
        if (uniqueCreatedItemsCount > 0) {
          createdMatchedLabel += ` (${uniqueCreatedItemsCount} new item)`
        }

        const statusLabels: string[] = []

        if (createdMatchedLabel !== "") {
          statusLabels.push(createdMatchedLabel)
        }

        for (const [status, count] of statusCount) {
          if (
            status === ItemMatchStatus.MANUAL_MATCH ||
            status === ItemMatchStatus.CREATE
          ) {
            continue
          }

          statusLabels.push(`${count} ${getItemMatchStatusLabel(status)}`)
        }

        summary.push({
          categoryName,
          decorator: this.matchDecorator(categoryName),
          statuses: statusLabels,
        })
      }

      return summary
    },
    currentCategoryAllIgnored(): boolean {
      const tabs = this.matchTabs

      if (this.currentMatchTab > tabs.length - 1) {
        return false
      }

      const current = tabs[this.currentMatchTab].title

      const items = this.itemMatchInfo.get(current)
      if (!items) {
        return false
      }

      return items.every(
        (item: ItemInfo) => item.status === ItemMatchStatus.TO_IGNORE
      )
    },
    getValidTargetsCount(): number {
      return this.validTargetsInvalidValues.reduce(
        (previousValue, currentValue) =>
          currentValue ? previousValue + 1 : previousValue,
        0
      )
    },
    getValidTimeColumns(): number[] {
      if (!this.targets || !this.file || !this.isDatasetTarget) {
        return []
      }

      const validTimeColumns = []
      const columnLength = this.file.scraped?.headers.length ?? 0
      for (let i = 0; i < columnLength; i++) {
        const columnValues = getValuesInColumn(i, this.file)
        if (
          columnValues.some((value) => {
            return isValidDate(value)
          })
        ) {
          validTimeColumns.push(i)
        }
      }
      return validTimeColumns
    },
    getImportDuration(): number {
      const importStartTime = this.durationAnalytics.get("importStartTime")
      if (!importStartTime) {
        return 0
      }

      return calculateTimeElapsed(importStartTime)
    },
    getPreviewDuration(): number {
      const previewStartTime =
        this.durationAnalytics.get("previewStartTime") ?? new Date().valueOf()
      const currentDuration = calculateTimeElapsed(previewStartTime)
      const previewCumulativeDuration =
        this.durationAnalytics.get("previewCumulativeDuration") ?? 0
      return currentDuration + previewCumulativeDuration
    },
  },
})

function isValidDate(value: string): boolean {
  const validFormats = ["d/M/y", "d-M-y", "M/d/y", "M-d-y", "y/M/d", "y-M-d"]
  return validFormats.some((format) => {
    return DateTime.fromFormat(value, format).isValid
  })
}

function isCategoryLocated(
  categoryName: CategoryName,
  locateFileTarget: LocateFileTarget,
  notInFileMap: Map<CategoryName, string>
): boolean {
  const locateColumn = locateFileTarget.get(categoryName)
  const notInFile = notInFileMap.get(categoryName)

  if (
    (locateColumn === undefined || locateColumn === NOT_IN_FILE) &&
    !notInFile
  ) {
    return false
  }

  return true
}

function isCategoryMatched(
  categoryName: CategoryName,
  itemMatchInfo: ItemMatchInfo
): boolean {
  // Channel is always considered matched (auto-created)
  if (categoryName === "Channel") {
    return true
  }
  const itemInfos = itemMatchInfo.get(categoryName)

  if (itemInfos === undefined) {
    return false
  }

  if (
    itemInfos.some((itemInfo) => itemInfo.status === ItemMatchStatus.PENDING)
  ) {
    return false
  }

  // If there's nothing to import, we are considered - not matched.
  return !itemInfos.every(
    (itemInfo) =>
      itemInfo.status === ItemMatchStatus.TO_IGNORE ||
      itemInfo.status === ItemMatchStatus.ALREADY_IGNORED
  )
}

// Only count an interaction as touched if the step and the component interaction are touched. This prevents showing the
// '?' status before the user has progressed beyond the locate step. The idea is that the UI should be able to present
// any invalid selections from being made, therefore the '?' status for locate really represents a required action by the
// user.
function getLocateDecorator(
  stepInteraction: InteractionInfo | undefined,
  componentInteraction: InteractionInfo | undefined,
  valid: boolean
): Decorator {
  if (!componentInteraction || !stepInteraction) {
    return Decorator.NOT_APPLICABLE
  }

  const combined = new InteractionInfo(
    componentInteraction.dirty,
    componentInteraction.isTouched() && stepInteraction.isTouched()
  )

  return getDecorator(combined, valid)
}

function removeNotInFileFromLocateInfo(
  locateInfo: LocateFileTarget
): LocateFileTarget {
  const locateInfoClone = new Map(locateInfo)
  for (const [key, value] of locateInfoClone) {
    if (value === NOT_IN_FILE) {
      locateInfoClone.delete(key)
    }
  }
  return locateInfoClone
}

function externalNamesFromMatchInfo(
  itemMatchInfo: ItemMatchInfo
): ExternalNames {
  const externalNames: ExternalNames = {}

  for (const [key, value] of itemMatchInfo) {
    // Find all the items to match
    const items = value.filter((item: ItemInfo) =>
      [
        ItemMatchStatus.AUTO_MATCH,
        ItemMatchStatus.MANUAL_MATCH,
        ItemMatchStatus.CREATE,
        ItemMatchStatus.EDIT_MATCH,
      ].includes(item.status)
    )

    if (items.length > 0) {
      // Format items to and object for the engine
      // { DATASET_NAME: { EXTERNAL_NAME : INTERNAL_NAME } }
      externalNames[key] = items.reduce(
        (result: Record<string, string>, item: ItemInfo) => {
          result[item.externalName] = item.internalName as string
          return result
        },
        {}
      )
    }
  }

  return externalNames
}

function ignoredNamesFromMatchInfo(itemMatchInfo: ItemMatchInfo): IgnoredNames {
  const ignoredNames: IgnoredNames = {}

  for (const [key, value] of itemMatchInfo) {
    // Find all the items to match
    const items = value.filter(
      (item: ItemInfo) => item.status === ItemMatchStatus.TO_IGNORE
    )

    if (items.length > 0) {
      // Format items to and object for the engine
      // { DATASET_NAME: [ EXTERNAL_NAME ] }
      ignoredNames[key] = items.map((item: ItemInfo) => item.externalName)
    }
  }

  return ignoredNames
}

export function getItemMatchStatusLabel(status: ItemMatchStatus) {
  const labelMap: Record<ItemMatchStatus, string> = {
    [ItemMatchStatus.EXISTING_MATCH]: "Existing match",
    [ItemMatchStatus.AUTO_MATCH]: "Auto match",
    [ItemMatchStatus.TO_IGNORE]: "Ignore",
    [ItemMatchStatus.MANUAL_MATCH]: "Match",
    [ItemMatchStatus.ALREADY_IGNORED]: "Already ignored",
    [ItemMatchStatus.PENDING]: "Pending",
    [ItemMatchStatus.CREATE]: "Match",
    [ItemMatchStatus.EDIT_MATCH]: "Edit match",
  }

  return labelMap[status] ?? ""
}

function newNamesFromMatchInfo(itemMatchInfo: ItemMatchInfo): NewNames {
  const newNames: NewNames = {}

  for (const [key, value] of itemMatchInfo) {
    // Find all the items to match
    const items = value.filter(
      (item: ItemInfo) => item.status === ItemMatchStatus.CREATE
    )

    if (items.length > 0) {
      // Format items to and object for the engine
      // { DATASET_NAME: [ names ]}
      newNames[key] = items.map((item) => item.internalName!)
    }
  }

  return newNames
}

function newNamesFromNotInFile(
  model: ModelFacade,
  notInFileMap: Map<CategoryName, string>
) {
  const newCategoryItems: NewNames = {}
  for (const categoryName in Object.fromEntries(notInFileMap)) {
    model.api.itemNames(categoryName).then((items: string[]) => {
      const value = notInFileMap.get(categoryName) as string

      if (!items.includes(value)) {
        newCategoryItems[categoryName] = [value]
      }
    })
  }
  return newCategoryItems
}

// calculates time elapsed in seconds since the given date time
function calculateTimeElapsed(startTime: number): number {
  const endTime = new Date()
  return Math.round((endTime.valueOf() - startTime) / 1000)
}

export function getValuesInColumn(
  columnIndex: number,
  file: UploadFile
): string[] {
  if (!file.scraped) {
    return []
  }
  return file.scraped.rows.map((row) => row[columnIndex])
}

export function isColumnEmpty(columnIndex: number, file: UploadFile): boolean {
  const nonBlankValues = getValuesInColumn(columnIndex, file).filter(
    (item) => item.trim().length !== 0
  )

  return nonBlankValues.length === 0
}
